import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ECoachChatSenderType, IChatMessage } from '@gray/domain/models/coach/chat-message.model';
import { CoachChatService } from '../../../core/services/coach-chat.service';
import { CoachChatSocketService, ICoachChatEvent } from '../../../core/services/coach-chat-socket.service';
import { CoachChatUnreadService } from '../../../core/services/coach-chat-unread.service';

const PAGE_SIZE = 30;

/**
 * The coach's conversation with a student.
 *
 * The thread itself is the shared chat component; this decides what is loaded and marks the
 * conversation read. It listens on the connection CoachChatUnreadService keeps open, which carries
 * every student's events, so only this student's are taken. Used by the student detail tab and by
 * the messages inbox.
 */
@Component({
  selector: 'coach-messages-tab',
  templateUrl: './messages-tab.component.html',
  styleUrl: './messages-tab.component.scss',
})
export class MessagesTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() studentId!: number;
  @Input() studentName?: string | null;

  /** Reported upwards so the tab bar can show how many messages are waiting. */
  @Output() unreadChanged = new EventEmitter<number>();

  messages: IChatMessage[] = [];
  page = 1;
  lastPage = 1;

  senderTypes = ECoachChatSenderType;

  isLoading = false;
  isSending = false;
  errorKey?: string;

  /** Known once the conversation head is loaded; a fallback match for events without a student id. */
  private conversationId: number | null = null;
  private activeStudentId: number | null = null;

  destroyed$ = new Subject();

  constructor(
    private chatService: CoachChatService,
    private chatSocket: CoachChatSocketService,
    private chatUnread: CoachChatUnreadService,
  ) {
  }

  ngOnInit(): void {
    this.listenSocket();
  }

  ngOnChanges(): void {
    if (this.studentId && this.studentId !== this.activeStudentId) {
      if (this.activeStudentId) {
        this.chatUnread.clearActiveStudent(this.activeStudentId);
      }
      this.activeStudentId = this.studentId;
      this.chatUnread.setActiveStudent(this.studentId);

      this.messages = [];
      this.page = 1;
      this.conversationId = null;
      this.getConversation();
      this.getMessages();
      // Older API builds only broadcast to conversation rooms; joining keeps them working.
      this.chatSocket.joinConversation(this.studentId);
    }
  }

  hasMore(): boolean {
    return this.page < this.lastPage;
  }

  getConversation() {
    this.chatService.getConversation(this.studentId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.conversationId = r.data?.conversationId ?? null;
        this.unreadChanged.emit(r.data?.unreadCount ?? 0);
        // Opening the tab is reading it.
        this.markRead();
      });
  }

  /** The API answers newest first; the thread reads oldest first. */
  getMessages(append = false) {
    this.isLoading = true;
    this.chatService.getMessages(this.studentId, `?page=${this.page}&size=${PAGE_SIZE}`)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const page = ((r.data?.result ?? []) as IChatMessage[]).slice().reverse();
        this.messages = append ? [...page, ...this.messages] : page;
        this.lastPage = r.data?.meta?.lastPage ?? 1;
      });
  }

  loadMore() {
    this.page += 1;
    this.getMessages(true);
  }

  send(body: string) {
    this.errorKey = undefined;
    this.isSending = true;

    this.chatService.sendMessage(this.studentId, body)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isSending = false))
      .subscribe({
        next: r => this.appendMessage(r.data as IChatMessage),
        // The API refuses two messages sent too close together, and anything over the length cap.
        error: error => {
          this.errorKey = (error?.code === 400)
            ? 'students.messages.error_rejected'
            : 'students.messages.error_failed';
        },
      });
  }

  private listenSocket() {
    // Normally already running from the layout; start() is a no-op then.
    this.chatUnread.start();

    // Rejoin after a reconnect, since conversation rooms do not survive it.
    this.chatSocket.onReady()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.studentId && this.chatSocket.joinConversation(this.studentId));

    this.chatSocket.onEvent()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: ICoachChatEvent) => {
        if (event?.event === 'message' && this.isOwnEvent(event.data)) {
          this.appendMessage(event.data as IChatMessage);
          this.markRead();
        }
      });
  }

  private isOwnEvent(data: { studentUserId?: number; conversationId?: number } | null): boolean {
    if (!data) {
      return false;
    }

    return data.studentUserId != null
      ? data.studentUserId === this.studentId
      : (this.conversationId != null && data.conversationId === this.conversationId);
  }

  /** The sender sees their own message twice — once from the response, once from the broadcast. */
  private appendMessage(message?: IChatMessage) {
    if (!message || this.messages.some(current => current.id === message.id)) {
      return;
    }

    this.messages = [...this.messages, message];
  }

  private markRead() {
    this.chatService.markRead(this.studentId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.unreadChanged.emit(0));
  }

  ngOnDestroy(): void {
    // The connection belongs to CoachChatUnreadService and stays open for the badge and inbox.
    if (this.activeStudentId) {
      this.chatUnread.clearActiveStudent(this.activeStudentId);
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
